/**
 * Local Education Agency (LEA) API Service
 */

import { api } from './client';
import type { LEAListItem, LEALogoResponse, LEAResponse, LogoCandidateList } from '../types/api';

export interface LEAListParams {
  skip?: number;
  limit?: number;
  search?: string;
  leaType?: string;
  operationalStatus?: string;
}

export interface LEACreateData {
  // Required
  nameOfInstitution: string;
  leaTypeDescriptorId: number;

  // Optional EdOrg fields
  shortNameOfInstitution?: string;
  webSite?: string;
  emailPattern?: string;
  faxNumber?: string;
  mainEmailAddress?: string;
  parentEducationOrganizationId?: number;
  operationalStatusDescriptorId?: number;

  // Optional LEA fields
  leaCategoryDescriptorId?: number;
  charterStatusDescriptorId?: number;
  districtsServed?: number[];
  associatedOrganizations?: number[];
  boardMeetingSchedule?: string;
  boardWebpage?: string;
}

export const leasApi = {
  /**
   * List all LEAs with optional filtering
   */
  list: async (params: LEAListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.skip) queryParams.set('skip', params.skip.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.leaType) queryParams.set('leaType', params.leaType);
    if (params.operationalStatus) queryParams.set('operationalStatus', params.operationalStatus);

    const query = queryParams.toString();
    return api.get<LEAListItem[]>(`/localEducationAgencies${query ? `?${query}` : ''}`);
  },

  /**
   * Get a single LEA by ID with full details
   * @param includeInactive - If true, includes ended/inactive staff assignments (superAdmin only)
   * @param includeClosedSchools - If true, includes schools with Closed/Inactive operational status (superAdmin only)
   */
  get: async (
    leaId: number,
    options: {
      includeStaff?: boolean;
      includeSchools?: boolean;
      includeInactive?: boolean;
      includeClosedSchools?: boolean;
      bustCache?: boolean;
    } = {}
  ) => {
    const {
      includeStaff = true,
      includeSchools = true,
      includeInactive = false,
      includeClosedSchools = false,
      bustCache = false,
    } = options;
    const params = new URLSearchParams();
    params.set('includeAdministrators', includeStaff.toString());
    params.set('includeSchools', includeSchools.toString());
    if (includeInactive) {
      params.set('includeInactive', 'true');
    }
    if (includeClosedSchools) {
      params.set('includeClosedSchools', 'true');
    }
    if (bustCache) {
      params.set('_t', Date.now().toString());
    }

    return api.get<LEAResponse>(`/localEducationAgencies/${leaId}?${params.toString()}`);
  },

  /**
   * Create a new LEA (superAdmin only)
   */
  create: async (data: LEACreateData) => {
    return api.post<LEAResponse>('/localEducationAgencies/', data);
  },

  /**
   * Update an LEA (superAdmin or organizationAdmin for own LEA)
   */
  update: async (leaId: number, data: any) => {
    return api.put<LEAResponse>(`/localEducationAgencies/${leaId}`, data);
  },

  /**
   * Get LEA staff
   */
  getStaff: async (leaId: number) => {
    return api.get<any[]>(`/localEducationAgencies/${leaId}/staff`);
  },

  /**
   * Add staff to LEA
   */
  addStaff: async (leaId: number, staffData: any) => {
    return api.post<any>(`/localEducationAgencies/${leaId}/staff`, staffData);
  },

  /**
   * Update staff assignment
   */
  updateStaff: async (leaId: number, assignmentId: number, updates: any) => {
    return api.put<any>(`/localEducationAgencies/${leaId}/staff/${assignmentId}`, updates);
  },

  /**
   * Remove staff from LEA
   */
  removeStaff: async (leaId: number, assignmentId: number) => {
    return api.delete(`/localEducationAgencies/${leaId}/staff/${assignmentId}`);
  },

  // ============================================================================
  // Address Management
  // ============================================================================

  /**
   * Add address to LEA
   */
  addAddress: async (leaId: number, addressData: any) => {
    return api.post<any>(`/localEducationAgencies/${leaId}/addresses`, addressData);
  },

  /**
   * Update LEA address
   */
  updateAddress: async (leaId: number, addressId: number, updates: any) => {
    return api.put<any>(`/localEducationAgencies/${leaId}/addresses/${addressId}`, updates);
  },

  /**
   * Remove address from LEA
   */
  removeAddress: async (leaId: number, addressId: number) => {
    return api.delete(`/localEducationAgencies/${leaId}/addresses/${addressId}`);
  },

  // ============================================================================
  // Phone Management
  // ============================================================================

  /**
   * Add phone to LEA
   */
  addPhone: async (leaId: number, phoneData: any) => {
    return api.post<any>(`/localEducationAgencies/${leaId}/phones`, phoneData);
  },

  /**
   * Update LEA phone
   */
  updatePhone: async (leaId: number, phoneId: number, updates: any) => {
    return api.put<any>(`/localEducationAgencies/${leaId}/phones/${phoneId}`, updates);
  },

  /**
   * Remove phone from LEA
   */
  removePhone: async (leaId: number, phoneId: number) => {
    return api.delete(`/localEducationAgencies/${leaId}/phones/${phoneId}`);
  },

  // ============================================================================
  // Email Address Management
  // ============================================================================

  /**
   * Add email address to LEA
   */
  addEmail: async (leaId: number, emailData: {
    emailAddress: string;
    emailTypeDescriptorId?: number | null;
    primaryEmailIndicator?: boolean;
  }) => {
    return api.post<any>(`/localEducationAgencies/${leaId}/emails`, emailData);
  },

  /**
   * Update LEA email address
   */
  updateEmail: async (leaId: number, emailId: number, updates: {
    emailAddress?: string;
    emailTypeDescriptorId?: number | null;
    primaryEmailIndicator?: boolean;
  }) => {
    return api.put<any>(`/localEducationAgencies/${leaId}/emails/${emailId}`, updates);
  },

  /**
   * Remove email address from LEA
   */
  removeEmail: async (leaId: number, emailId: number) => {
    return api.delete(`/localEducationAgencies/${leaId}/emails/${emailId}`);
  },

  // ============================================================================
  // Identification Code Management
  // ============================================================================

  /**
   * Add identification code to LEA (superAdmin only)
   */
  addIdentificationCode: async (leaId: number, data: {
    identificationCode: string;
    educationOrganizationIdentificationSystemDescriptor?: number | null;
    assigningOrganizationIdentificationCode?: string | null;
  }) => {
    return api.post<any>(`/localEducationAgencies/${leaId}/identificationCodes`, data);
  },

  /**
   * Update LEA identification code (superAdmin only)
   */
  updateIdentificationCode: async (leaId: number, codeId: number, updates: {
    identificationCode?: string;
    educationOrganizationIdentificationSystemDescriptor?: number | null;
    assigningOrganizationIdentificationCode?: string | null;
  }) => {
    return api.put<any>(`/localEducationAgencies/${leaId}/identificationCodes/${codeId}`, updates);
  },

  /**
   * Remove identification code from LEA (superAdmin only)
   */
  removeIdentificationCode: async (leaId: number, codeId: number) => {
    return api.delete(`/localEducationAgencies/${leaId}/identificationCodes/${codeId}`);
  },

  // ============================================================================
  // Logo
  // ============================================================================

  /**
   * The LEA's logo as an image Blob. `version` (logoUpdatedAt) makes a
   * changed logo a new URL, so the browser cache never serves a stale one.
   */
  getLogo: async (leaId: number, version: string): Promise<Blob> => {
    return api.getBlob(`/localEducationAgencies/${leaId}/logo?v=${encodeURIComponent(version)}`);
  },

  /**
   * Add or replace the LEA's logo (superAdmin or organizationAdmin for own LEA)
   */
  uploadLogo: async (leaId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.putForm<LEALogoResponse>(`/localEducationAgencies/${leaId}/logo`, form);
  },

  /**
   * Look for the LEA's logo on its website (superAdmin only). Nothing is
   * saved; takes up to about 20 seconds.
   */
  getLogoCandidates: async (leaId: number) => {
    return api.get<LogoCandidateList>(`/localEducationAgencies/${leaId}/logoCandidates`);
  },

  /**
   * Download an image (a candidate's URL or any public image URL) and make
   * it the LEA's logo (superAdmin only)
   */
  setLogoFromUrl: async (leaId: number, url: string) => {
    return api.post<LEALogoResponse>(`/localEducationAgencies/${leaId}/logo/fromUrl`, { url });
  },

  /**
   * Remove the LEA's logo (superAdmin or organizationAdmin for own LEA)
   */
  removeLogo: async (leaId: number) => {
    return api.delete(`/localEducationAgencies/${leaId}/logo`);
  },

  // ============================================================================
  // Export
  // ============================================================================

  /**
   * Export LEAs to XLSX or CSV
   */
  export: async (params: { format?: 'xlsx' | 'csv'; search?: string; leaType?: string } = {}): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    queryParams.set('format', params.format || 'xlsx');
    if (params.search) queryParams.set('search', params.search);
    if (params.leaType) queryParams.set('leaType', params.leaType);

    return api.getBlob(`/localEducationAgencies/export?${queryParams.toString()}`);
  }
};
